import {
  ToggleGroup,
  ToggleGroupItem,
} from '@listening/frontend/components/radix/toggle-group';
import { useAtomValue } from '@effect/atom-react';
import type { GetConversionDefaultsV2200 } from '@listening/api-contract/schemas';
import { conversionDefaultsAtom } from '@listening/frontend/query/conversion-defaults';
import { AsyncResult } from 'effect/reactivity';
import { SettingsRowsSkeleton } from '@/components/web-fe/design-v2/skeletons';
import { createFileRoute } from '@tanstack/react-router';
import {
  type ConversionOcrProviderPreference,
  type ConversionPipelinePreference,
  type ConversionVisionPreference,
  useConversionPipelinePreferenceStore,
} from '@/stores/conversion-pipeline-preference';

// Dev/staging-only settings page (the hub entry is gated by clientEnv.conversionPipelineToggleEnabled).
// Sections are ordered the way the pipeline runs: pipeline choice, OCR, then vision layout.
// @see docs/projects/active/conversion-pipeline-v2/web-app-tooling.md
export const Route = createFileRoute(
  '/app/_authenticated/_dashboard/settings/conversion',
)({
  component: Page,
  pendingComponent: SettingsRowsSkeleton,
  pendingMs: 0,
});

interface PreferenceOption<T extends string> {
  value: T;
  label: string;
  hint: string;
}

function PreferenceSection<T extends string>({
  title,
  description,
  options,
  value,
  onChange,
  serverDefault,
  inactive = false,
}: {
  title: string;
  description: string;
  options: ReadonlyArray<PreferenceOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** The resolved server default, rendered under the 'default' option. */
  serverDefault?: string | undefined;
  /** Grey the section out — v1 is frozen, so v2-only options have no effect under it. */
  inactive?: boolean;
}) {
  return (
    <div className='flex flex-col gap-6'>
      {/* Disabled ToggleGroup items dim themselves (the item variants' disabled: styles),
          so only the header needs manual dimming. */}
      <div className={`flex flex-col gap-1 ${inactive ? 'opacity-50' : ''}`}>
        <h2
          className='text-[15px] text-[var(--v2-text-primary)]'
          style={{ fontWeight: 'var(--v2-heading-weight)' }}
        >
          {title}
        </h2>
        <p className='text-[13px] text-[var(--v2-text-muted)]'>{description}</p>
        {inactive && (
          <p className='text-[13px] font-medium text-[var(--v2-text-muted)]'>
            Inactive — uploads currently use the frozen v1 pipeline, so this
            option has no effect.
          </p>
        )}
      </div>
      <ToggleGroup
        type='single'
        className='flex flex-wrap gap-2'
        value={value}
        disabled={inactive}
        onValueChange={(val) => {
          if (val) onChange(val as T);
        }}
      >
        {options.map((opt) => (
          <ToggleGroupItem
            key={opt.value}
            value={opt.value}
            className='flex h-auto flex-col items-start gap-0.5 rounded-[var(--v2-button-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] bg-[var(--v2-page-bg)] px-4 py-3 text-left data-[state=on]:border-[var(--v2-accent)] data-[state=on]:bg-[var(--v2-accent)]/5 data-[state=on]:text-[var(--v2-accent)]'
          >
            <span className='text-sm font-medium'>{opt.label}</span>
            <span className='text-xs text-[var(--v2-text-muted)]'>
              {opt.hint}
            </span>
            {opt.value === 'default' && serverDefault && (
              <span className='text-xs font-medium text-[var(--v2-text-muted)]'>
                Currently: {serverDefault}
              </span>
            )}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

const SERVER_DEFAULT_HINT = 'Use whatever the backend is configured for.';

const PIPELINE_OPTIONS: ReadonlyArray<
  PreferenceOption<ConversionPipelinePreference>
> = [
  {
    value: 'default',
    label: 'Server default',
    hint: 'Use whatever pipeline the backend is configured for.',
  },
  {
    value: 'v1',
    label: 'v1 (current)',
    hint: 'Frozen production pipeline (Azure OCR).',
  },
  {
    value: 'v2',
    label: 'v2 (new)',
    hint: 'Modular OCR pipeline under evaluation.',
  },
];

const OCR_PROVIDER_OPTIONS: ReadonlyArray<
  PreferenceOption<ConversionOcrProviderPreference>
> = [
  { value: 'default', label: 'Server default', hint: SERVER_DEFAULT_HINT },
  {
    value: 'azure',
    label: 'Azure',
    hint: 'Azure Document Intelligence (what v1 uses).',
  },
  {
    value: 'paddle-modal',
    label: 'Paddle (Modal)',
    hint: 'Self-hosted PP-StructureV3 on Modal.',
  },
  {
    value: 'paddleocr-vl-modal',
    label: 'PaddleOCR-VL (Modal)',
    hint: 'Experimental vision-language OCR on Modal. Has no native word geometry, so word highlight boxes are synthesized in an extra detection pass.',
  },
];

const VISION_OPTIONS: ReadonlyArray<
  PreferenceOption<ConversionVisionPreference>
> = [
  {
    value: 'default',
    label: 'Server default',
    hint: 'Let the backend decide (v2 skips vision by default).',
  },
  {
    value: 'on',
    label: 'Vision on',
    hint: 'Force the vision layout model on for this device.',
  },
  {
    value: 'off',
    label: 'Vision off',
    hint: 'Force the vision layout model off for this device.',
  },
];

// Human-readable "Currently: …" strings for each section's Server-default option,
// from the dev/staging-gated defaults endpoint. Undefined while loading (or if the
// fetch fails) — the option then just shows its generic hint.
function serverDefaultLabels(
  data: typeof GetConversionDefaultsV2200.Type | undefined,
) {
  if (!data) return {};
  return {
    pipeline: data.pipeline_version,
    vision: `${data.vision_v2_enabled ? 'on' : 'off'} for v2 (v1 always on)`,
    ocrProvider:
      data.ocr_provider === 'paddle-modal'
        ? `paddle-modal (${data.paddle_mode})`
        : data.ocr_provider,
  };
}

function Page() {
  const store = useConversionPipelinePreferenceStore();
  const defaults = AsyncResult.getOrElse(
    useAtomValue(conversionDefaultsAtom),
    () => undefined,
  );
  const serverDefaults = serverDefaultLabels(defaults);

  // What new uploads from this device will actually run: the explicit choice, or the server
  // default when the preference is 'default'. v1 is frozen (it ignores every override below),
  // so all v2-only sections go inactive when this resolves to v1. Unknown until the defaults
  // load — leave the sections active rather than flashing them off.
  const effectivePipeline =
    store.preference === 'default'
      ? defaults?.pipeline_version
      : store.preference;
  const v1Active = effectivePipeline === 'v1';

  return (
    <div className='flex flex-col gap-[var(--v2-gap-6)]'>
      <PreferenceSection
        title='Conversion pipeline'
        description='Dev/staging only. Choose which conversion pipeline new uploads from this device should request. The pipeline that actually ran is shown as a badge in the library and reader.'
        options={PIPELINE_OPTIONS}
        value={store.preference}
        onChange={store.setPreference}
        serverDefault={serverDefaults.pipeline}
      />
      <PreferenceSection
        title='OCR provider (v2 only)'
        description='Dev/staging only. Which OCR provider a v2 conversion runs on. No effect on v1 uploads (v1 is frozen on Azure).'
        options={OCR_PROVIDER_OPTIONS}
        value={store.ocrProvider}
        onChange={store.setOcrProvider}
        serverDefault={serverDefaults.ocrProvider}
        inactive={v1Active}
      />
      <PreferenceSection
        title='Vision model (v2 only)'
        description='Dev/staging only. v2 skips the vision layout model by default (an ablation found its output changed almost nothing). Force it on or off for new uploads from this device to compare. v1 always runs it.'
        options={VISION_OPTIONS}
        value={store.vision}
        onChange={store.setVision}
        serverDefault={serverDefaults.vision}
        inactive={v1Active}
      />
    </div>
  );
}
